import { useMemo, useState } from "react";
import {
  Box,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import { useTenantTranslation } from "hooks/formatters";
import { useFormatNumber } from "src/utils/formatNumber";
import { HeatSeries } from "src/store/api/generatedApi";
import { ProportionalLayerStack } from "./ProportionalLayerStack";
import {
  BasketLayeringComparison,
  DisplayLayer,
  buildLayeringComparison,
} from "./layeringComparison";

const PLANNED_MIX_SOURCE = "plannedSource";
const REPORTED_MIX_SOURCE = "level2";
const SCRAP_EYE_MIX_SOURCE = "ScrapEye";
const STACK_HEIGHT = 360;

// The layering sources shown in the tab, in display order.
const SOURCE_ORDER = [
  PLANNED_MIX_SOURCE,
  REPORTED_MIX_SOURCE,
  SCRAP_EYE_MIX_SOURCE,
];
const SOURCE_LABEL_KEY: Record<string, string> = {
  [PLANNED_MIX_SOURCE]: "plannedSource",
  [REPORTED_MIX_SOURCE]: "reported",
  [SCRAP_EYE_MIX_SOURCE]: "scrapEye",
};

const layersForSource = (
  basket: BasketLayeringComparison,
  source: string
): DisplayLayer[] => {
  if (source === PLANNED_MIX_SOURCE) return basket.planned;
  if (source === REPORTED_MIX_SOURCE) return basket.reported;
  return basket.measured;
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { ease: "easeOut" as const },
  },
};

export const HeatLayeringComplianceTab = ({
  heatData,
}: {
  heatData: HeatSeries;
}) => {
  const { t } = useTenantTranslation();
  const formatInteger = useFormatNumber()(0);
  const asPercent = (score: number): string => formatInteger(score * 100);
  const sourceLabel = (source: string): string =>
    t(SOURCE_LABEL_KEY[source] ?? source);

  const baskets = useMemo(() => {
    const basketsForSource = (source: string) =>
      heatData.mixes.find((mix) => mix.source === source)?.mix.baskets ?? [];
    return buildLayeringComparison(
      basketsForSource(PLANNED_MIX_SOURCE),
      basketsForSource(REPORTED_MIX_SOURCE),
      basketsForSource(SCRAP_EYE_MIX_SOURCE)
    );
  }, [heatData.mixes]);

  const pairs = heatData.layering_compliance;
  const [selectedPairIndex, setSelectedPairIndex] = useState(0);
  // The compared pair drives which two columns sit together with the score; any other
  // source is shown to the side. With no scored pairs, all sources are shown flat.
  // Clamp the index: it persists across heat navigation (the drawer isn't remounted),
  // so a heat with fewer pairs must not select an out-of-range / stale entry.
  const effectivePairIndex =
    selectedPairIndex < pairs.length ? selectedPairIndex : 0;
  const selectedPair = pairs[effectivePairIndex];
  const comparedSources = selectedPair
    ? [selectedPair.target_mix_source, selectedPair.comparison_mix_source]
    : SOURCE_ORDER;
  const otherSources = SOURCE_ORDER.filter(
    (source) => !comparedSources.includes(source)
  );

  if (baskets.length === 0) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ p: 2 }}>
        {t("noBasketData")}
      </Typography>
    );
  }

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <Stack spacing={2} sx={{ px: 1, mt: 1 }}>
        {selectedPair ? (
          <motion.div variants={rowVariants}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="h4" sx={{ fontWeight: "fontWeightBold" }}>
                {t("layeringComplianceValue", {
                  percent: asPercent(selectedPair.compliance.overall),
                })}
              </Typography>
              {pairs.length > 1 ? (
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={effectivePairIndex}
                  onChange={(_, value: number | null) => {
                    if (value !== null) setSelectedPairIndex(value);
                  }}
                >
                  {pairs.map((pair, index) => (
                    <ToggleButton
                      key={`${pair.target_mix_source}-${pair.comparison_mix_source}`}
                      value={index}
                    >
                      {t("layeringComparisonLabel", {
                        target: sourceLabel(pair.target_mix_source),
                        comparison: sourceLabel(pair.comparison_mix_source),
                      })}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              ) : null}
            </Stack>
          </motion.div>
        ) : null}
        {baskets.map((basket) => {
          const basketCompliance = selectedPair?.compliance.baskets.find(
            (entry) => entry.basket_number === basket.basketNumber
          );
          return (
            <motion.div key={basket.basketNumber} variants={rowVariants}>
              <Box
                sx={{
                  borderRadius: 2,
                  p: 2,
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: "fontWeightBold", mb: 2 }}
                >
                  {t("basket")} {basket.basketNumber}
                </Typography>
                <Stack direction="row" spacing={3} alignItems="stretch">
                  {comparedSources.map((source) => (
                    <ProportionalLayerStack
                      key={source}
                      title={sourceLabel(source)}
                      layers={layersForSource(basket, source)}
                      scaleMass={basket.scaleMass}
                      containerHeight={STACK_HEIGHT}
                    />
                  ))}
                  {basketCompliance ? (
                    <Stack
                      spacing={2}
                      justifyContent="center"
                      sx={{ minWidth: 110, pl: 1 }}
                    >
                      <Stack spacing={0.25}>
                        <Typography variant="caption" color="text.secondary">
                          {t("overall")}
                        </Typography>
                        <Typography
                          variant="h3"
                          sx={{ fontWeight: "fontWeightBold" }}
                        >
                          {t("compliancePercent", {
                            percent: asPercent(basketCompliance.compliance),
                          })}
                        </Typography>
                      </Stack>
                      <Stack spacing={0.25}>
                        <Typography variant="caption" color="text.secondary">
                          {t("order")}
                        </Typography>
                        <Typography variant="h5">
                          {t("compliancePercent", {
                            percent: asPercent(basketCompliance.sequence_score),
                          })}
                        </Typography>
                      </Stack>
                      <Stack spacing={0.25}>
                        <Typography variant="caption" color="text.secondary">
                          {t("mass")}
                        </Typography>
                        <Typography variant="h5">
                          {t("compliancePercent", {
                            percent: asPercent(basketCompliance.mass_score),
                          })}
                        </Typography>
                      </Stack>
                    </Stack>
                  ) : null}
                  {selectedPair && otherSources.length > 0 ? (
                    <>
                      <Divider orientation="vertical" flexItem />
                      {otherSources.map((source) => (
                        <ProportionalLayerStack
                          key={source}
                          title={sourceLabel(source)}
                          layers={layersForSource(basket, source)}
                          scaleMass={basket.scaleMass}
                          containerHeight={STACK_HEIGHT}
                        />
                      ))}
                    </>
                  ) : null}
                </Stack>
              </Box>
            </motion.div>
          );
        })}
      </Stack>
    </motion.div>
  );
};
